if (!customElements.get('bundle-selector')) {
  class BundleSelector extends HTMLElement {
    connectedCallback() {
      try {
        const jsonEl = this.querySelector('[data-bundle-data]');
        const data = JSON.parse(jsonEl.textContent);
        this.bundles = data.bundles;
      } catch (e) {
        console.error('[BundleSelector] Failed to parse bundle data:', e);
        return;
      }

      this.selectedBundleIndex = null;
      this.selectedVariantId = null;
      this.pendantPrice = parseInt(this.dataset.pendantPrice, 10);
      this.originalImage = null;
      this.availability = {};
      this.originalPriceHTML = null;

      this.cardsContainer = this.querySelector('.hl-bundle-selector__cards');
      this.lengthsContainer = this.querySelector('.hl-bundle-selector__lengths');
      this.saveContainer = this.querySelector('.hl-bundle-selector__save');
      this.sectionId = this.dataset.sectionId;

      this.bindCardClicks();
      this.bindKeyboard();
      this.interceptFormSubmit();
      this.checkAvailability();
      this.subscribeToVariantChange();
    }

    /* ── Card selection ── */

    bindCardClicks() {
      this.querySelectorAll('.hl-bundle-selector__card').forEach((card) => {
        card.addEventListener('click', () => {
          if (card.classList.contains('is-unavailable')) return;
          const index = card.dataset.bundleIndex;
          if (index === 'none') {
            this.selectPendantOnly();
          } else {
            this.selectBundle(parseInt(index, 10));
          }
        });
      });
    }

    selectBundle(index) {
      this.selectedBundleIndex = index;
      const bundle = this.bundles[index];

      // Update card states
      this.querySelectorAll('.hl-bundle-selector__card').forEach((card) => {
        const isTarget = parseInt(card.dataset.bundleIndex, 10) === index;
        card.classList.toggle('is-selected', isTarget);
        card.setAttribute('aria-checked', isTarget ? 'true' : 'false');
      });
      // Deselect pendant-only card
      const pendantCard = this.querySelector('[data-bundle-index="none"]');
      pendantCard.classList.remove('is-selected');
      pendantCard.setAttribute('aria-checked', 'false');

      // Populate length pills
      this.renderLengthPills(bundle);

      // Pre-select first available variant
      const firstAvailable = bundle.chain_variants.find(
        (v) => this.availability[this.extractNumericId(v.id)] !== false
      ) || bundle.chain_variants[0];
      this.selectLength(firstAvailable, bundle);

      // Show save message
      this.saveContainer.hidden = false;
      this.querySelector('[data-save-percent]').textContent = bundle.discount_percentage;

      // Swap image
      this.swapImage(bundle.composite_image_url);
    }

    selectPendantOnly() {
      this.selectedBundleIndex = null;
      this.selectedVariantId = null;

      // Update card states
      this.querySelectorAll('.hl-bundle-selector__card').forEach((card) => {
        const isPendant = card.dataset.bundleIndex === 'none';
        card.classList.toggle('is-selected', isPendant);
        card.setAttribute('aria-checked', isPendant ? 'true' : 'false');
      });

      // Hide length pills and save message
      this.lengthsContainer.hidden = true;
      this.saveContainer.hidden = true;

      // Restore original price
      this.restoreOriginalPrice();

      // Restore original image
      this.restoreOriginalImage();
    }

    /* ── Length picker ── */

    renderLengthPills(bundle) {
      if (bundle.chain_variants.length <= 1) {
        this.lengthsContainer.hidden = true;
        return;
      }

      this.lengthsContainer.innerHTML = bundle.chain_variants
        .map((v) => {
          const numId = this.extractNumericId(v.id);
          const isDisabled = this.availability[numId] === false;
          return `<button
            type="button"
            class="hl-bundle-selector__length-btn"
            data-variant-id="${numId}"
            data-variant-price="${v.price}"
            ${isDisabled ? 'disabled' : ''}
          >${v.title}</button>`;
        })
        .join('');

      this.lengthsContainer.hidden = false;

      // Bind click handlers
      this.lengthsContainer.querySelectorAll('.hl-bundle-selector__length-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const variant = bundle.chain_variants.find(
            (v) => this.extractNumericId(v.id) === btn.dataset.variantId
          );
          if (variant) this.selectLength(variant, bundle);
        });
      });
    }

    selectLength(variant, bundle) {
      const numId = this.extractNumericId(variant.id);
      this.selectedVariantId = numId;

      // Update pill selection
      this.lengthsContainer.querySelectorAll('.hl-bundle-selector__length-btn').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.variantId === numId);
      });

      // Update price display
      this.updatePrice(variant, bundle);
    }

    /* ── Price calculation ── */

    updatePrice(chainVariant, bundle) {
      const chainPricePence = Math.round(parseFloat(chainVariant.price) * 100);
      const bundleTotal = this.pendantPrice + chainPricePence;
      const discountedTotal = Math.round(bundleTotal * (100 - bundle.discount_percentage) / 100);
      const saveAmount = bundleTotal - discountedTotal;

      // Update the product price display
      const priceRow = document.querySelector(
        `#price-${this.sectionId} .hl-product-price__row`
      );
      if (priceRow) {
        if (!this.originalPriceHTML) {
          this.originalPriceHTML = priceRow.innerHTML;
        }
        priceRow.innerHTML = `
          <span class="hl-product-price__current">${this.formatMoney(discountedTotal)}</span>
          <span class="hl-product-price__compare">${this.formatMoney(bundleTotal)}</span>
          <span class="hl-product-price__save">SAVE ${this.formatMoney(saveAmount)}</span>
        `;
      }

      // Update save message
      this.querySelector('[data-save-amount]').textContent = this.formatMoney(saveAmount);

      // Update ATC button price (MutationObserver on old element breaks when innerHTML replaced)
      const atcPrice = document.querySelector('[data-product-price]');
      if (atcPrice) atcPrice.textContent = this.formatMoney(discountedTotal);
    }

    restoreOriginalPrice() {
      if (!this.originalPriceHTML) return;
      const priceRow = document.querySelector(
        `#price-${this.sectionId} .hl-product-price__row`
      );
      if (priceRow) {
        priceRow.innerHTML = this.originalPriceHTML;
        this.originalPriceHTML = null;
      }
      // Restore ATC button price to pendant price
      const atcPrice = document.querySelector('[data-product-price]');
      if (atcPrice) atcPrice.textContent = this.formatMoney(this.pendantPrice);
    }

    formatMoney(subunits) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
      try {
        return new Intl.NumberFormat('en', { style: 'currency', currency: currency }).format(subunits / 100);
      } catch (e) {
        return `£${(subunits / 100).toFixed(2)}`;
      }
    }

    /* ── Image swap ── */

    swapImage(url) {
      const img = document.querySelector('[data-gallery-scroll] .hl-gallery__image');
      if (!img) return;

      if (!this.originalImage) {
        this.originalImage = { src: img.src, srcset: img.srcset || '' };
      }

      img.srcset = '';
      img.src = url;
      img.onerror = () => this.restoreOriginalImage();
    }

    restoreOriginalImage() {
      if (!this.originalImage) return;
      const img = document.querySelector('[data-gallery-scroll] .hl-gallery__image');
      if (!img) return;
      img.src = this.originalImage.src;
      img.srcset = this.originalImage.srcset;
    }

    /* ── Availability check ── */

    async checkAvailability() {
      const fetches = this.bundles.map(async (bundle) => {
        if (!bundle.chain_handle) return;
        try {
          const res = await fetch(`/products/${bundle.chain_handle}.js`);
          if (!res.ok) throw new Error(res.status);
          const product = await res.json();

          bundle.chain_variants.forEach((v) => {
            const numId = this.extractNumericId(v.id);
            const shopifyVariant = product.variants.find((sv) => String(sv.id) === numId);
            this.availability[numId] = shopifyVariant ? shopifyVariant.available : false;
          });

          // Use chain product's featured image as fallback if no composite image
          if (product.featured_image) {
            const card = this.querySelector(`[data-bundle-index="${this.bundles.indexOf(bundle)}"]`);
            const img = card?.querySelector('[data-chain-handle] , .hl-bundle-selector__image img');
            if (img && !bundle.composite_image_url) {
              img.src = product.featured_image;
            }
          }
        } catch {
          // Treat as unavailable if fetch fails
          bundle.chain_variants.forEach((v) => {
            this.availability[this.extractNumericId(v.id)] = false;
          });
        }
      });

      await Promise.all(fetches);
      this.applyAvailability();
    }

    applyAvailability() {
      this.bundles.forEach((bundle, index) => {
        const allUnavailable = bundle.chain_variants.every(
          (v) => this.availability[this.extractNumericId(v.id)] === false
        );

        if (allUnavailable) {
          const card = this.querySelector(`[data-bundle-index="${index}"]`);
          if (!card) return;
          card.classList.add('is-unavailable');
          card.setAttribute('aria-disabled', 'true');
          card.removeAttribute('tabindex');

          // Replace price with "Sold Out"
          const infoEl = card.querySelector('.hl-bundle-selector__info');
          const priceEl = infoEl.querySelector('.hl-bundle-selector__price');
          const compareEl = infoEl.querySelector('.hl-bundle-selector__compare-price');
          if (priceEl) priceEl.style.display = 'none';
          if (compareEl) compareEl.style.display = 'none';

          const soldOut = document.createElement('span');
          soldOut.className = 'hl-bundle-selector__sold-out';
          soldOut.textContent = 'Sold Out';
          infoEl.appendChild(soldOut);
        }
      });
    }

    /* ── Cart intercept ── */

    interceptFormSubmit() {
      const form = this.closest('form[data-type="add-to-cart-form"]')
        || document.querySelector(`#MainProduct-${this.sectionId} form[data-type="add-to-cart-form"]`);
      if (!form) return;

      this.form = form;
      this.submitBtn = form.querySelector('[type="submit"]');

      form.addEventListener('submit', async (e) => {
        if (this.selectedBundleIndex === null) return; // let normal flow handle it

        e.preventDefault();
        e.stopImmediatePropagation();

        if (!this.selectedVariantId) return;

        const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);
        const pendantVariantId = parseInt(form.querySelector('[name="id"]').value, 10);
        const chainVariantId = parseInt(this.selectedVariantId, 10);
        const bundle = this.bundles[this.selectedBundleIndex];
        const discountPercentage = bundle.discount_percentage;

        // Show loading state
        this.setLoading(true);

        try {
          const body = {
            items: (() => {
              const groupId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
              return [
                {
                  id: pendantVariantId,
                  quantity: quantity,
                  properties: {
                    '_bundle': 'true',
                    '_bundle_group': groupId,
                    '_bundle_discount': String(discountPercentage),
                  },
                },
                {
                  id: chainVariantId,
                  quantity: quantity,
                  properties: {
                    '_bundle_pendant_id': String(pendantVariantId),
                    '_bundle_group': groupId,
                    '_bundle_discount': String(discountPercentage),
                    '_bundle': 'true',
                  },
                },
              ];
            })(),
            sections: this.getSectionsToRender().map((s) => s.id),
            sections_url: window.location.pathname,
          };

          const res = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(body),
          });

          if (!res.ok) {
            const error = await res.json();
            throw new Error(error.description || error.message || 'Could not add to cart');
          }

          const response = await res.json();

          // Normalise response for cart drawer
          const normalisedState = {
            id: response.items?.[0]?.variant_id ?? null,
            sections: response.sections || {},
          };

          // Publish cart update for other listeners (before render, matching product-form.js)
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-selector',
              cartData: response,
            });
          }

          // Open cart drawer
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && normalisedState.sections && Object.keys(normalisedState.sections).length) {
            if (cartDrawer.classList.contains('is-empty')) cartDrawer.classList.remove('is-empty');
            cartDrawer.setActiveElement(document.activeElement);
            cartDrawer.renderContents(normalisedState);
          } else if (!normalisedState.sections || !Object.keys(normalisedState.sections).length) {
            // Graceful fallback: reload page if sections missing from response
            window.location.reload();
            return;
          }
          // Reset to pendant-only after successful bundle add
          this.selectPendantOnly();

        } catch (error) {
          // Show error message — create a temporary toast since the custom theme
          // does not have a built-in error container in the product form
          console.error('[BundleSelector] Cart add error:', error);
          let errorEl = this.form.querySelector('.hl-bundle-error');
          if (!errorEl) {
            errorEl = document.createElement('div');
            errorEl.className = 'hl-bundle-error';
            errorEl.style.cssText = `
              font-family: var(--font-caption, 'Roboto Mono', monospace);
              font-size: 12px; color: var(--color-urgent-red, #BF3834);
              margin-top: 8px; text-align: center;
            `;
            this.submitBtn.parentElement.insertBefore(errorEl, this.submitBtn.nextSibling);
          }
          errorEl.textContent = error.message;
          errorEl.hidden = false;
          setTimeout(() => { errorEl.hidden = true; }, 5000);
        } finally {
          this.setLoading(false);
        }
      }, { capture: true });
    }

    setLoading(loading) {
      if (!this.submitBtn) return;
      if (loading) {
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('loading');
      } else {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('loading');
      }
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' },
      ];
    }

    /* ── Variant change listener ── */

    subscribeToVariantChange() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        // Read new pendant price from the variant change event data
        // product-info.js publishes: { data: { sectionId, html, variant } }
        const newVariant = event.data?.variant;
        if (newVariant?.price) {
          this.pendantPrice = newVariant.price;
        }

        // Reset original image so next swap stores the new one
        this.originalImage = null;

        // Reset original price HTML so restore picks up the re-rendered markup
        this.originalPriceHTML = null;

        // Recalculate if bundle is selected
        if (this.selectedBundleIndex !== null) {
          const bundle = this.bundles[this.selectedBundleIndex];
          const chainVariant = bundle.chain_variants.find(
            (v) => this.extractNumericId(v.id) === this.selectedVariantId
          );
          if (chainVariant) {
            this.updatePrice(chainVariant, bundle);
            this.swapImage(bundle.composite_image_url);
          }
        }
      });
    }

    /* ── Keyboard navigation ── */

    bindKeyboard() {
      this.cardsContainer.addEventListener('keydown', (e) => {
        if (!['ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(e.key)) return;
        e.preventDefault();

        const cards = [...this.querySelectorAll('.hl-bundle-selector__card:not(.is-unavailable)')];
        const currentIndex = cards.indexOf(document.activeElement);

        if (e.key === 'ArrowLeft' && currentIndex > 0) {
          cards[currentIndex - 1].focus();
        } else if (e.key === 'ArrowRight' && currentIndex < cards.length - 1) {
          cards[currentIndex + 1].focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          document.activeElement.click();
        }
      });
    }

    /* ── Helpers ── */

    extractNumericId(gid) {
      return String(gid).split('/').pop();
    }
  }

  customElements.define('bundle-selector', BundleSelector);
}

/* ── Bundle quantity + remove handlers (runs globally for cart drawer) ── */

// Bundle quantity +/- buttons
async function updateBundleQty(container, delta) {
  const display = container.querySelector('.hl-bundle-qty__display');
  const currentQty = parseInt(display.textContent, 10);
  const newQty = Math.max(0, currentQty + delta);
  const index1 = parseInt(container.dataset.pendantIndex, 10);
  const index2 = parseInt(container.dataset.chainIndex, 10);

  // Disable buttons and show loading spinner (same pattern as Dawn cart.js)
  const row = container.closest('.hl-bundle-group');
  container.querySelectorAll('button').forEach((b) => b.setAttribute('disabled', ''));
  display.textContent = newQty;
  if (row) {
    const spinner = row.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    const priceWrapper = row.querySelector('.cart-item__price-wrapper');
    if (priceWrapper) priceWrapper.style.visibility = 'hidden';
  }

  try {
    if (newQty === 0) {
      const higher = Math.max(index1, index2);
      const lower = Math.min(index1, index2);
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: higher, quantity: 0 }),
      });
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lower, quantity: 0 }),
      });
    } else {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: index1, quantity: newQty }),
      });
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: index2, quantity: newQty }),
      });
    }

    const sectionsRes = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
    const sections = await sectionsRes.json();
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) cartDrawer.renderContents({ id: null, sections });
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-qty' });
  } catch (err) {
    console.error('[BundleQty]', err);
    window.location.reload();
  }
}

document.addEventListener('click', (e) => {
  const minusBtn = e.target.closest('[data-bundle-minus]');
  const plusBtn = e.target.closest('[data-bundle-plus]');
  if (minusBtn) {
    e.preventDefault();
    updateBundleQty(minusBtn.closest('.hl-bundle-qty'), -1);
  } else if (plusBtn) {
    e.preventDefault();
    updateBundleQty(plusBtn.closest('.hl-bundle-qty'), 1);
  }
});

// Bundle remove handler — capture phase to fire before Dawn's cart-remove-button
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.hl-bundle-remove');
  if (!btn) return;

  e.preventDefault();
  e.stopImmediatePropagation();

  const index1 = btn.dataset.pendantIndex;
  const index2 = btn.dataset.chainIndex;

  const row = btn.closest('.hl-bundle-group');
  btn.setAttribute('disabled', 'true');
  if (row) {
    const spinner = row.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    const priceWrapper = row.querySelector('.cart-item__price-wrapper');
    if (priceWrapper) priceWrapper.style.visibility = 'hidden';
  }

  try {
    // Remove both items by setting quantity to 0 via /cart/change.js
    // Remove higher index first so lower index stays valid
    const higher = Math.max(parseInt(index1), parseInt(index2));
    const lower = Math.min(parseInt(index1), parseInt(index2));

    let res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: higher, quantity: 0 }),
    });
    if (!res.ok) throw new Error('Failed to remove bundle item');

    res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: lower, quantity: 0 }),
    });

    if (!res.ok) throw new Error('Failed to remove bundle');

    // Fetch updated cart sections and re-render drawer
    const sectionsRes = await fetch(
      `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`
    );
    const sections = await sectionsRes.json();

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.renderContents({ id: null, sections });
    }

    if (typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-remove' });
    }
  } catch (err) {
    console.error('[BundleRemove]', err);
    window.location.reload();
  }
}, { capture: true });
